<template>
  <div :class="{'d-none': !isShown}">
    <div v-show="expandsHorizontally" class="hidden">
      <label ref="textCopy">{{displayText}}</label>
    </div>
    <textarea
      ref="textarea"
      :name="name"
      :id="id"
      v-bind="textareaBindings"
      v-model.sync="localValue"
      v-show="isShown"
      @click="emitClick"
      @blur="emitBlur"
      @mousedown="$emit('mousedown', $event)"
      :wrap="wrapVal"
      @keydown="handleKeydown"
      @paste="expandAndEmit($event, 'paste')"
      @keyup="expand"
      :placeholder="placeholder"
      :readonly="isReadOnly"
      :disabled="isDisabled || disabled"
      v-tooltip.wrap="tooltipIfDisabled"
      :maxlength="maxlength"
      rows="1"
      class="label skip-drag"
      :class="{ 'read-only' : isReadOnly, disabled: isDisabled }"
      :style="textareaStyle"
    ></textarea>
  </div>
</template>

<script lang="ts">
  import Vue from "vue";
  import localValueEmissionMixin from "../mixins/localValueEmissionMixin";
  import disabledMixin from '~/mixins/disabledMixin'

  export default Vue.extend({
    mixins: [
      localValueEmissionMixin,
      disabledMixin,
    ],
    props: {
      value: String,
      name: {
        type: String
      },
      id: {
        type: String
      },
      isMultiline: {
        type: Boolean,
        default: false
      },
      placeholder: {
        type: String,
        default: ''
      },
      minLines: {
        type: Number,
        default: 1,
      },
      maxHeight: {
        type: Number,
        default: 10000
      },
      isShown: {
        type: Boolean,
        default: true
      },
      isDisabled: {
        type: Boolean,
        default: false
      },
      isReadOnly: {
        type: Boolean,
        default: false
      },
      expandsHorizontally: {
        type: Boolean,
        default: false
      },
      maxlength: {
        type: Number
      },
      textareaStyle: {
        type: Object,
      },
      /**
       * To generally bind anything to the textarea DOM element
       */
      textareaBindings: {
        type: Object,
        default: () => ({}),
      },
    },
    mounted() {
      if (this.isShown) {
        if (this.expandsHorizontally) {
          this.expandAfterLoadTimeout = setTimeout(() => this.expand(), 200); //hack cause fonts have a delay loading time, throwing off initial calculation
        }
        this.expand({ overrideLocked: true });
      }
    },
    updated() {
      this.expand({ overrideLocked: true });
    },
    beforeDestroy() {
      clearTimeout(this.expandAfterLoadTimeout);
    },
    computed: {
      wrapVal() {
        if (this.isMultiline) {
          return 'hard'
        } else {
          return 'soft'
        }
      },
      displayText() {
        if ( this.localValue === null || this.localValue === undefined ||this.localValue.length === 0) {
          return this.placeholder;
        }
        return this.localValue;
      }
    },
    data: function(){
      return {
        $textCopy: null
      }
    },
    methods: {
      expand({ overrideLocked } = { overrideLocked: false }) {
        if (!overrideLocked && (this.isDisabled || this.isReadOnly)) return

        let textarea = this.$refs.textarea;
        if (this.expandsHorizontally) {
          if (!this.$textCopy) {
            this.setUpHorizontalExpansion();
          }
          textarea.style.width = 'inherit';
          textarea.style.width = (this.$textCopy.outerWidth()) + 'px';
        }
        textarea.style.height = 'inherit';
        textarea.style.height = getNewHeight(textarea, this.minLines, this.maxHeight) + 'px';
        this.$emit('update:textarea-height', textarea.style.height)
      },
      expandAndEmit(event, type) {
        if (this.isDisabled || this.isReadOnly) return

        this.expand();
        this.$emit(type, event);
      },
      emitClick(event) {
        this.$emit('click', event);
      },
      emitBlur(event) {
        this.$emit('blur', event);
      },
      handleKeydown(event) {
        if (this.isDisabled || this.isReadOnly) return

        if (event.key === 'Enter') {
          if (!this.isMultiline) {
            event.preventDefault();
          }
          this.$emit('keydown:enter', event);
        } else {
          this.$emit('keydown', event);
        }
      },
      focus() {
        this.$refs.textarea.focus();
      },
      setUpHorizontalExpansion() {
        this.$textCopy = $(this.$refs.textCopy);
        let $textarea = $(this.$refs.textarea);
        let padding = $textarea.outerWidth() - $textarea.width() + 20;
        this.$textCopy.css({
          'width': 'auto',
          'font': $textarea.css('font'),
          'padding-left': padding,
        });
      },
    },
    watch: {
      isShown(newVal: boolean) {
        if (newVal) {
          this.expand();
        }
      }
    }
  });

  function getMinHeight(textarea: HTMLTextAreaElement, minLines: number): number {
    const styles = window.getComputedStyle(textarea)
    const lineHeight = parseFloat(styles.getPropertyValue('line-height'))
    const borderY = parseFloat(styles.getPropertyValue('border-top-width')) +
      parseFloat(styles.getPropertyValue('border-bottom-width'))
    const paddingY = parseFloat(styles.getPropertyValue('padding-top')) +
      parseFloat(styles.getPropertyValue('padding-bottom'))
    return lineHeight * minLines + borderY + paddingY
  }

  function getNewHeight(textarea: HTMLTextAreaElement, minLines: number, maxHeight: number): number {
    const minHeight = getMinHeight(textarea, minLines)

    const $textarea = $(textarea)
    const oldOverflowY = textarea.style.overflowY
    textarea.style.overflowY = 'clip'
    const borderHeight = $textarea.outerHeight() - $textarea.innerHeight()
    const desiredHeight = Math.max(textarea.scrollHeight + borderHeight, $textarea.height())
    textarea.style.overflowY = oldOverflowY

    return Math.max(minHeight, Math.min(maxHeight, desiredHeight))
  }
</script>
